import "./style.scss";

import { Tooltip } from "@mui/material";
import type { TAdvancePayment } from "components/AdvanceManagement/NewAdvanceModal";
import type { BasicTableColumn } from "components/shared/BasicTable";
import BasicTable from "components/shared/BasicTable";
import { formatLocaleDateString, formatNumber } from "helpers/utils";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

type TSlice = TAdvancePayment["slices"][number];

interface IAdvanceSlicesRecap<T extends TSlice = TSlice> {
    /**
     * Inline advance payment slices (amounts in cents).
     */
    slices: T[];
    /** Optional class name per row (e.g. period impact highlight). */
    getRowClassName?: (row: T, index: number) => string | undefined;
    /** Optional columns appended after the default ones (e.g. period impact hint). */
    trailingColumns?: BasicTableColumn<T>[];
    /** Center-align the slice index column (default: left). */
    centerIndexColumn?: boolean;
}

/**
 * Minimal recap table for inline advance payment slices.
 * Displays amount (converted from cents to euros), date, comment and status.
 */
const AdvanceSlicesRecap = <T extends TSlice = TSlice>({
    slices,
    getRowClassName,
    trailingColumns,
    centerIndexColumn = false,
}: IAdvanceSlicesRecap<T>) => {
    const [t] = useTranslation();

    const columns = useMemo<BasicTableColumn<T>[]>(
        () => [
            {
                header: t("TermRecap.SliceIndex"),
                cellClassName: "slice-index",
                align: centerIndexColumn ? "center" : "left",
                render: (_, i, rows) => `${i + 1}/${rows.length}`,
            },
            {
                header: t("TermRecap.SliceAmount"),
                align: "right",
                render: (row) => formatNumber(+row.amount / 100, { isCurrency: true, always2Digit: true }),
            },
            {
                header: t("TermRecap.SliceDate"),
                align: "right",
                render: (row) =>
                    row.paymentDate
                        ? formatLocaleDateString({
                              date: row.paymentDate as string,
                              options: { numeric: true, timeZone: "UTC" },
                          })
                        : "-",
            },
            {
                header: t("TermRecap.SliceComment"),
                cellClassName: "slice-comment",
                render: (row) => {
                    const comment = row.comment?.trim();
                    if (!comment) return "-";
                    return (
                        <Tooltip title={comment} arrow>
                            <span className="slice-comment-text">{comment}</span>
                        </Tooltip>
                    );
                },
            },
            {
                header: t("TermRecap.SliceStatus"),
                cellClassName: "slice-status-cell",
                render: (row) => (
                    <span
                        className={`slice-status slice-status--${
                            row.status === 2 ? "paid" : row.status === 1 ? "topay" : "notpaid"
                        }`}
                    >
                        {row.status === 2
                            ? t("AdvancePayment.Paid")
                            : row.status === 1
                              ? t("Filters.ForTransfer")
                              : t("AdvancePayment.NotPaid")}
                    </span>
                ),
            },
            ...(trailingColumns ?? []),
        ],
        [t, trailingColumns, centerIndexColumn],
    );

    return (
        <BasicTable<T>
            columns={columns}
            rows={slices}
            className="advance-slices-recap"
            getRowClassName={getRowClassName}
        />
    );
};

export default AdvanceSlicesRecap;
